import { isTpuLabellingEnabled } from "@/features/branding";
import { decryptData } from "@/features/cryptography";
import { SupabaseDatabase } from "@/features/supabase";
import { facadesUrl } from "@/store/services/facades";
import { GetTpuSubunitRequest, GetTpuSubunitResponse, supabaseApi } from "@/store/services/supabase";
import { dispatchWithRetries } from "@/store/services/supabase/utils";
import { PortfolioExportAssetData, PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { SecretSalt } from "@/types/cryptography";
import { hexToArrayBuffer } from "@/utils/crypto";
import { Effect, pipe } from "effect";
import type { Distributor, LineReducerOutput, ResponseSuccessWithMetadata } from "../schema";
import { IndexedDb } from "../utils/indexeddb";
import { lineReducer } from "./line_reducer";

type PortfolioAsset = SupabaseDatabase["public"]["Tables"]["portfolio_assets"]["Row"];

const BATCH_SIZE = 100;
const CONCURRENCY_LIMIT = 3;
const MAX_RETRIES = 10;
const RETRY_TIMEOUT = 3000;

export interface StartProps {
  schema: PortfolioExportJobSchema;
  incrementDownloadCount: () => void;
  incrementProcessedCount: (value: number) => void;
  distributor: Distributor;
}

export const start = async ({ schema, incrementProcessedCount, incrementDownloadCount, distributor }: StartProps) => {
  const totalLines = schema.assets.totalCount;
  const iDbConfig = {
    dbName: schema.name,
    storeName: schema.id,
    keyPath: "id",
  };

  try {
    const downloadBatch = async (
      batch: { id: string; data: PortfolioExportAssetData }[],
      attempt = 0
    ): Promise<void> => {
      if (batch.length === 0) return;

      try {
        const streamResponse = await streamResults(
          batch.map(({ id }) => id),
          schema.stamps
        );

        if (!streamResponse.body) {
          throw new Error("No response body received from stream");
        }

        const { results, tpuRequest } = await processStream(streamResponse.body, batch, incrementDownloadCount);

        const resultsWithTpuIfEnabled = await buildResultsWithTpuIfEnabled(results, tpuRequest);
        await IndexedDb.addItems(iDbConfig, resultsWithTpuIfEnabled);

        const resultIds = new Set(results.map(({ item_id }) => item_id));
        const failed = batch.filter(({ id }) => !resultIds.has(id));

        if (failed.length > 0) {
          if (attempt < MAX_RETRIES) {
            console.warn(`Retrying ${failed.length} assets (attempt ${attempt + 1})`);
            await new Promise((resolve) => setTimeout(resolve, RETRY_TIMEOUT));
            return downloadBatch(failed, attempt + 1);
          } else {
            console.error(`Max retries exceeded for ${failed.length} assets`);
            throw new Error(`Failed to download ${failed.length} assets after ${MAX_RETRIES} retries`);
          }
        }
      } catch (err) {
        if (attempt < MAX_RETRIES) {
          console.warn(`Error processing batch (attempt ${attempt + 1}): ${err}`);
          await new Promise((resolve) => setTimeout(resolve, RETRY_TIMEOUT));
          return downloadBatch(batch, attempt + 1);
        } else {
          console.error(`Max retries exceeded for batch: ${err}`);
          throw err;
        }
      }
    };

    const offsets = Array.from({ length: Math.ceil(totalLines / BATCH_SIZE) }, (_, i) => i * BATCH_SIZE);

    const getBatch = (offset: number) =>
      pipe(
        Effect.promise(() => pullAssets(schema.id, offset, BATCH_SIZE, schema.secret)),
        Effect.flatMap((results) => Effect.promise(() => downloadBatch(results)))
      );

    const batches = Effect.forEach(offsets, getBatch, { concurrency: CONCURRENCY_LIMIT });
    await Effect.runPromise(batches);

    // Process all downloaded assets
    await IndexedDb.getAllItemsBatched(
      iDbConfig,
      async (batch: ResponseSuccessWithMetadata[]) => {
        await distributor.send(batch);
        incrementProcessedCount(batch.length);
      },
      BATCH_SIZE
    ).finally(() => {
      console.log("All items processed.");
      incrementProcessedCount(totalLines);
    });
  } catch (error: any) {
    console.error("Error in start function:", error);
    throw new Error(error.message || "Unknown error occurred");
  } finally {
    // Allow time for any pending operations to settle
    await new Promise((resolve) => setTimeout(resolve, 500));
    await IndexedDb.closeDatabase(iDbConfig);
    distributor.send(undefined);
  }
};

const processStream = async (
  body: ReadableStream<Uint8Array>,
  assets: { id: string; data: PortfolioExportAssetData }[],
  incrementDownloadCount: () => void
): Promise<{
  results: ResponseSuccessWithMetadata[];
  tpuRequest: GetTpuSubunitRequest;
}> => {
  const results: ResponseSuccessWithMetadata[] = [];
  const tpuRequest: GetTpuSubunitRequest = {};

  // Pipe output through a decoder stream, to avoid doing manually.
  const reader = body.pipeThrough(new TextDecoderStream()).getReader();

  // Caches previous unfinished value to build an object across multiple chunks.
  let previousLine = "";
  let chunk = await reader.read();

  while (!chunk.done) {
    const data = chunk.value
      .split("\n")
      .map((line) => (line.startsWith("\x1E") ? line.slice(1) : line))
      .reduce<LineReducerOutput>(lineReducer, {
        previousLine,
        successes: [],
        errors: [],
      });

    results.push(
      ...data.successes.map((result) => {
        const metadata = assets.find(({ id }) => id === result.item_id);
        if (!metadata) {
          throw new Error(
            "An error occurred while calling some asset-level results from our database. Some asset-level data may be missing from your file. Please contact XDI Support with the time/date of your analysis so we can troubleshoot."
          );
        }
        tpuRequest[result.item_id] = [
          result.result.metadata.location.latitude,
          result.result.metadata.location.longitude,
        ];

        incrementDownloadCount();

        return {
          ...result,
          metadata: metadata!.data,
        };
      })
    );
    previousLine = data.previousLine;

    // Read the next chunk.
    chunk = await reader.read();
  }

  if (previousLine) {
    try {
      results.push(JSON.parse(previousLine));
    } catch {
      console.warn("Could not parse last line fragment:", previousLine);
    }
  }

  return { results, tpuRequest };
};

const buildResultsWithTpuIfEnabled = async (
  results: ResponseSuccessWithMetadata[],
  tpuRequest: GetTpuSubunitRequest
) => {
  if (!isTpuLabellingEnabled()) return results;

  const data = await dispatchWithRetries<GetTpuSubunitResponse>({
    action: supabaseApi.endpoints.getTpuLabelling.initiate(tpuRequest),
    maxRetries: 10,
    timeout: 3000,
  });

  return results.map((item) => ({
    ...item,
    metadata: {
      ...item.metadata,
      hk: {
        tpu: data?.[item.item_id]?.tpu || "No Data",
        subunit: data?.[item.item_id]?.subunit || "No Data",
      },
    },
  }));
};

const pullAssets = async (portfolioId: string, offset: number, length: number, secret: SecretSalt) => {
  const action = supabaseApi.endpoints.getSuccessfulPortfolioAssets.initiate({
    portfolioId,
    start: offset,
    length,
  });
  const data = await dispatchWithRetries<PortfolioAsset[]>({ action, maxRetries: 10, timeout: 5000 });
  return Promise.all(
    data.map(async (asset) => {
      try {
        const decryptedData = await decryptData(secret, hexToArrayBuffer(asset.data!));
        const decryptedId = await decryptData(secret, hexToArrayBuffer(asset.encrypted_id));

        return {
          id: decryptedId,
          data: JSON.parse(decryptedData) as PortfolioExportAssetData,
        };
      } catch {
        throw new Error(
          "Failed to decrypt assets - try logging in and out again. If errors persist, clear your local storage and cache for this page."
        );
      }
    })
  );
};

// Retry stream on network failures
export const streamResults = async (
  ids: string[],
  stamps: string[],
  maxRetries = 10,
  timeout = 3000
): Promise<Response> => {
  let retryCount = 0;
  let error: Error | null = null;

  console.log(`[Download] Streaming results for ${ids.length} assets.`);

  while (retryCount <= maxRetries) {
    try {
      const url = new URL(`${facadesUrl}structural/comprehensive/results`);
      if (stamps.length) {
        stamps.forEach((stamp) => url.searchParams.append("stamps", stamp));
      }

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          Authorization: `Basic ${process.env.NEXT_PUBLIC_FACADES_ABCDE_API_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ids),
      });

      if (!response.ok) {
        throw new Error(`Stream request failed with status: ${response.status}`);
      }

      return response;
    } catch (e: unknown) {
      retryCount++;
      error = e instanceof Error ? e : new Error(String(e));

      if (retryCount > maxRetries) {
        console.error(`Maximum retries reached for streamResults`);
        break;
      }

      console.warn(`Retry attempt ${retryCount}/${maxRetries} for streamResults failed: ${error?.message}`);
      await new Promise((resolve) => setTimeout(resolve, timeout));
    }
  }

  throw new Error(`Failed to stream results after ${maxRetries} attempts: ${error?.message}`);
};
